import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Input,
  Select,
  Badge,
  DataTable,
  ConfirmDialog,
  Alert,
  Skeleton,
  ErrorBoundary,
  EmptyState,
  Toggle,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import type { UsageMetric } from '@bbpos/types';
import { tenantService } from '@/services/tenant.service';
import { TenantStatusBadge } from '@/components/features/tenants/TenantStatusBadge';
import { useKeyboardShortcut } from '@/hooks/use-keyboard-shortcut';
import type { TenantStatus } from '@/types/tenant';
import { USAGE_METRIC_LABELS } from '@/components/features/subscriptions/plan-entitlements';

// ─── Design tokens ──────────────────────────────────────────────────────────
const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

// ─── Filter options (spec 3.3: all lifecycle statuses) ──────────────────────
const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PROVISIONED', label: 'Provisioned' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'TRIAL', label: 'Trial' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'RESTAURANT', label: 'Restaurant' },
];

// ─── Table column defs (spec: Name, Type, Status, Plan, Locations, Users, Created)
interface TenantRow {
  id: string;
  business_name: string;
  business_type: string;
  status: string;
  subscription_plan?: string;
  owner_email?: string;
  location_count?: number;
  user_count?: number;
  /** Metrics at or over 100 % of their limit this month. */
  over_limit_metrics: UsageMetric[];
  created_at: string;
  trial_ends_at?: string;
}

const columns: ColumnDef<TenantRow>[] = [
  {
    key: 'business_name',
    header: 'Tenant',
    render: (v, row) => (
      <div>
        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{v as string}</p>
        <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{row.owner_email ?? '—'}</p>
      </div>
    ),
  },
  {
    key: 'business_type',
    header: 'Type',
    render: (v) => (
      <Badge variant={(v as string) === 'RETAIL' ? 'info' : 'success'} size="sm">
        {v as string}
      </Badge>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (v) => <TenantStatusBadge status={v as TenantStatus} />,
  },
  {
    key: 'subscription_plan',
    header: 'Plan',
    render: (v) => (
      <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
        {(v as string) ?? '—'}
      </span>
    ),
  },
  {
    key: 'over_limit_metrics',
    header: 'Limits',
    render: (_v, row) =>
      row.over_limit_metrics.length > 0 ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
          <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
          Over limit: {row.over_limit_metrics.map((m) => USAGE_METRIC_LABELS[m] ?? m).join(', ')}
        </span>
      ) : (
        <span className="text-xs text-neutral-400 dark:text-neutral-500">—</span>
      ),
  },
  {
    key: 'location_count',
    header: 'Locations',
    numeric: true,
    render: (v) => <span className="tabular-nums text-sm">{(v as number | undefined) ?? '—'}</span>,
  },
  {
    key: 'user_count',
    header: 'Users',
    numeric: true,
    render: (v) => <span className="tabular-nums text-sm">{(v as number | undefined) ?? '—'}</span>,
  },
  {
    key: 'created_at',
    header: 'Created',
    render: (v) => (
      <span className="tabular-nums text-xs text-neutral-500 dark:text-neutral-400">
        {new Date(v as string).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
      </span>
    ),
  },
];

// ─── Content ────────────────────────────────────────────────────────────────

function TenantListContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const searchRef = useRef<HTMLInputElement>(null);

  const page = Number(searchParams.get('page') ?? '1');
  const statusFilter = searchParams.get('status') ?? '';
  const typeFilter = searchParams.get('business_type') ?? '';
  const searchQuery = searchParams.get('search') ?? '';
  const overLimitFilter = searchParams.get('over_limit') === 'true';

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'suspend' | 'cancel';
    tenantId: string;
    tenantName?: string;
  } | null>(null);

  // Keyboard shortcuts (spec 3.3)
  useKeyboardShortcut('/', useCallback(() => {
    searchRef.current?.focus();
  }, []));
  useKeyboardShortcut('n', useCallback(() => {
    navigate('/tenants/new');
  }, [navigate]));

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = new URLSearchParams(searchParams);
      if (searchInput) params.set('search', searchInput);
      else params.delete('search');
      params.set('page', '1');
      setSearchParams(params);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchParams, setSearchParams]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['tenants', page, statusFilter, typeFilter, searchQuery, overLimitFilter],
    queryFn: () =>
      tenantService.list({
        page,
        limit: 20,
        status: statusFilter || undefined,
        business_type: typeFilter || undefined,
        search: searchQuery || undefined,
        over_limit: overLimitFilter || undefined,
      }),
    staleTime: 15_000,
  });

  const suspendMutation = useMutation({
    mutationFn: (id: string) => tenantService.updateStatus(id, 'SUSPENDED'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setConfirmAction(null);
      toast({ title: 'Tenant suspended', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => tenantService.softDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setConfirmAction(null);
      toast({ title: 'Tenant cancelled', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  const provisionMutation = useMutation({
    mutationFn: (id: string) => tenantService.provision(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast({ title: 'Tenant approved and provisioned', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  function setFilter(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    params.set('page', '1');
    setSearchParams(params);
  }

  const tenants: TenantRow[] = (data?.data ?? []).map((t) => ({
    id: t.id,
    business_name: t.business_name,
    business_type: t.business_type,
    status: t.status,
    subscription_plan: t.subscription_plan,
    owner_email: t.owner_email,
    location_count: t.location_count,
    user_count: t.user_count,
    over_limit_metrics: t.over_limit_metrics ?? [],
    created_at: t.created_at,
    trial_ends_at: t.trial_ends_at,
  }));

  if (error) {
    return (
      <Alert
        variant="danger"
        title="Failed to load tenants"
        description="An error occurred loading the tenant list. Please try refreshing."
        action={{ label: 'Retry', onClick: () => queryClient.invalidateQueries({ queryKey: ['tenants'] }) }}
      />
    );
  }

  const hasNoData =
    !isLoading && tenants.length === 0 && !searchQuery && !statusFilter && !typeFilter && !overLimitFilter;
  const totalPages = data?.meta ? Math.ceil(data.meta.total / data.meta.limit) : 1;

  // Action column with inline actions
  const actionColumn: ColumnDef<TenantRow> = {
    key: 'id',
    header: '',
    render: (_v, row) => (
      <div className="flex gap-1.5 justify-end">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/tenants/${row.id}`)}>
          View
        </Button>
        {row.status === 'PENDING_APPROVAL' && (
          <Button variant="primary" size="sm" onClick={() => provisionMutation.mutate(row.id)}>
            Approve
          </Button>
        )}
        {(row.status === 'ACTIVE' || row.status === 'TRIAL') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmAction({ type: 'suspend', tenantId: row.id, tenantName: row.business_name })}
          >
            Suspend
          </Button>
        )}
        {row.status !== 'CANCELLED' && row.status !== 'SUSPENDED' && row.status !== 'PENDING_APPROVAL' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmAction({ type: 'cancel', tenantId: row.id, tenantName: row.business_name })}
          >
            Cancel
          </Button>
        )}
      </div>
    ),
  };

  return (
    <div className="animate-fade-in">
      {/* Filter Bar — glass panel */}
      <div className={cn(GLASS_PANEL, 'p-4 mb-6')}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="w-full sm:w-72">
            <Input
              ref={searchRef}
              variant="search"
              placeholder="Search by name, email, tax ID..."
              value={searchInput}
              onChange={setSearchInput}
            />
          </div>
          <Select
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setFilter('status', Array.isArray(v) ? v[0] : v)}
            placeholder="Status"
            className="w-44"
          />
          <Select
            options={TYPE_OPTIONS}
            value={typeFilter}
            onChange={(v) => setFilter('business_type', Array.isArray(v) ? v[0] : v)}
            placeholder="Type"
            className="w-40"
          />
          <Toggle
            id="tenants-over-limit"
            label="Over limit"
            checked={overLimitFilter}
            onChange={(on) => setFilter('over_limit', on ? 'true' : '')}
          />
          {(statusFilter || typeFilter || searchQuery || overLimitFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchInput('');
                setSearchParams(new URLSearchParams());
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Empty state for brand-new platform */}
      {hasNoData ? (
        <div className="animate-slide-up">
          <EmptyState
            variant="page"
            title="No tenants yet"
            description="Create your first tenant to start managing businesses on the platform"
            action={{ label: 'Create Tenant', onClick: () => navigate('/tenants/new') }}
            icon={
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500/20 to-indigo-500/20">
                <svg className="h-8 w-8 text-brand-600 dark:text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            }
          />
        </div>
      ) : isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} />
          ))}
        </div>
      ) : (
        <div className="animate-slide-up">
          <DataTable
            columns={[...columns, actionColumn]}
            data={tenants}
            loading={isLoading}
            variant="default"
            emptyState={{ title: 'No tenants found', description: 'Try adjusting your search or filters' }}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-neutral-500 dark:text-neutral-400">
              <span className="tabular-nums">
                Page {page} of {totalPages} ({data?.meta.total ?? 0} total)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('page', String(page - 1));
                    setSearchParams(params);
                  }}
                >
                  Previous
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('page', String(page + 1));
                    setSearchParams(params);
                  }}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirm Dialog — stronger warning for cancel */}
      {confirmAction && (
        <ConfirmDialog
          open
          title={confirmAction.type === 'suspend' ? 'Suspend Tenant' : 'Cancel Tenant'}
          description={
            confirmAction.type === 'suspend'
              ? `"${confirmAction.tenantName}" will enter read-only mode. All mutations will be blocked until reactivated.`
              : `This will cancel "${confirmAction.tenantName}" and begin the data lifecycle process (90-day hard delete, 120-day S3 cleanup). This action is extremely hard to reverse.`
          }
          confirmLabel={confirmAction.type === 'suspend' ? 'Suspend' : 'Cancel Tenant'}
          variant="danger"
          loading={suspendMutation.isPending || cancelMutation.isPending}
          onConfirm={() => {
            if (confirmAction.type === 'suspend') {
              suspendMutation.mutate(confirmAction.tenantId);
            } else {
              cancelMutation.mutate(confirmAction.tenantId);
            }
          }}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}

export function TenantListPage() {
  const navigate = useNavigate();

  return (
    <PageContainer
      title="Tenant Management"
      subtitle="Manage all business tenants on the platform"
      breadcrumbs={[{ label: 'Tenants' }]}
      actions={
        <Button variant="primary" onClick={() => navigate('/tenants/new')}>
          + New Tenant
        </Button>
      }
    >
      <ErrorBoundary>
        <TenantListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
